import React, {FC, useMemo} from 'react'
import {useNavigate} from 'react-router-dom'
import CommerciumLoaderHost from 'src/utils/CommerciumLoaderHost'
import {PlatformDataType} from 'src/interfaces/Platform.interface'
import {
  buildPendingActionPath,
  DashboardPendingAction,
} from '../utils/pendingActionNavigation'
import DashboardTotalOrdersCard from './DashboardTotalOrdersCard'

type StatData = {
  orders_sync_errors: number
  pending_orders_count: number
  unlinked_items_count: number
  to_be_shipped_count: number
  inventory_mismatch_count: number
  synced_sku_count: number
}

type PendingAction = {
  label: string
  value: number
  badgeClass: string
  action: DashboardPendingAction
}

type Props = {
  statData: StatData
  statDataLoading?: boolean
  platformId: string
  connectedPlatforms: PlatformDataType[]
  totalOrders: number
  ordersTrendPercent: number | null
  ordersSummaryLoading?: boolean
}

const DashboardSidebar: FC<Props> = ({
  statData,
  statDataLoading = false,
  platformId,
  connectedPlatforms,
  totalOrders,
  ordersTrendPercent,
  ordersSummaryLoading = false,
}) => {
  const navigate = useNavigate()

  const navigateToPendingAction = (action: DashboardPendingAction) => {
    const path = buildPendingActionPath(action, platformId, connectedPlatforms)
    if (path) navigate(path)
  }

  const actions: PendingAction[] = useMemo(
    () => [
      {
        label: 'Failed Order Sync',
        value: statData.orders_sync_errors,
        badgeClass: 'dashboard-badge-danger',
        action: 'failed_order_sync',
      },
      {
        label: 'Linked Items',
        value: statData.synced_sku_count,
        badgeClass: 'dashboard-badge-info',
        action: 'linked_items',
      },
      {
        label: 'Unlinked Items',
        value: statData.unlinked_items_count,
        badgeClass: 'dashboard-badge-warning',
        action: 'unlinked_items',
      },
      {
        label: 'Pending Orders',
        value: statData.pending_orders_count,
        badgeClass: 'dashboard-badge-info',
        action: 'pending_orders',
      },
      {
        label: 'To Be Shipped',
        value: statData.to_be_shipped_count,
        badgeClass: 'dashboard-badge-info',
        action: 'to_be_shipped',
      },
      {
        label: 'Inventory Mismatch',
        value: statData.inventory_mismatch_count,
        badgeClass: 'dashboard-badge-danger',
        action: 'inventory_mismatch',
      },
    ],
    [statData]
  )

  return (
    <aside>
      <DashboardTotalOrdersCard
        totalOrders={totalOrders}
        percentChange={ordersTrendPercent}
        loading={ordersSummaryLoading}
      />

      <div className='dashboard-panel-card'>
        <div className='dashboard-panel-header' style={{background: '#fafbfc'}}>
          <h3 style={{color: '#475569'}}>Pending Actions</h3>
        </div>
        <div className='dashboard-panel-body dashboard-action-list py-2'>
          {actions.map((action) => {
            const path = buildPendingActionPath(action.action, platformId, connectedPlatforms)
            const isNonClickable = action.action === 'linked_items' || action.action === 'unlinked_items' || action.action === 'inventory_mismatch'
            const isClickable = !isNonClickable && Boolean(path)
            const actionContent = (
              <>
                <span className='dashboard-action-label fw-semibold text-dark'>
                  {action.label}
                </span>
                <span className={`dashboard-badge ${action.badgeClass}`}>
                  {statDataLoading ? (
                    <CommerciumLoaderHost variant='inline' layout='inline' />
                  ) : (
                    action.value
                  )}
                </span>
              </>
            )

            return (
              <div key={action.label} className='dashboard-action-item'>
                {isClickable ? (
                  <button
                    type='button'
                    className='dashboard-action-row w-100 border-0 bg-transparent text-start'
                    onClick={() => navigateToPendingAction(action.action)}
                  >
                    {actionContent}
                  </button>
                ) : (
                  <div className='dashboard-action-row'>{actionContent}</div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </aside>
  )
}

export default DashboardSidebar